import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Badge,
  InputGroup,
  Form,
  Pagination,
  Modal,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import {
  useGetContactDataQuery,
  useDisableContactMutation,
} from "../../../redux/api/ContactApi/contactApi";
import DeleteModel from "../../../Components/DeleteModel";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import {
  FaSearch,
  FaPhoneAlt,
  FaEnvelope,
  FaCalendarAlt,
  FaTrashAlt,
  FaUser,
  FaAddressBook,
  FaCheckCircle,
  FaCopy,
  FaEye,
} from "react-icons/fa";
import { message } from "antd";
import "./Contacts.css";

const Contacts = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);

  const page = Number(searchParams.get("page")) || 1;
  const search = searchParams.get("search") || "";
  const limit = Number(searchParams.get("limit")) || 10;

  // Local state for input to ensure smooth typing
  const [searchTerm, setSearchTerm] = useState(search);

  // Sync local state if search param changes externally
  useEffect(() => {
    setSearchTerm(search);
  }, [search]);

  // Debounced search update
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm !== search) {
        setSearchParams({
          page: 1,
          limit,
          search: searchTerm,
        });
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm, limit, search, setSearchParams]);

  const { data, isLoading, isFetching } = useGetContactDataQuery(
    {
      page,
      limit,
      search,
    },
    {
      pollingInterval: 15000,
    },
  );

  const [disableContact] = useDisableContactMutation();

  const handleView = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedContact(null);
  };

  const contacts = data?.data || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const handleRemove = (contact) => {
    setContactToDelete(contact);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await disableContact(contactToDelete.id).unwrap();
      setShowDeleteModal(false);
      setContactToDelete(null);
      message.success("Contact removed successfully!");
    } catch (error) {
      console.error("Failed to remove contact:", error);
      message.error("Failed to remove contact.");
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setContactToDelete(null);
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaAddressBook className="text-primary me-2" />
            Contact Management
          </h2>
          <p className="text-muted mb-0">
            Monitor and response to your inquiries
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 bg-white overflow-hidden h-100">
            <Card.Body className="d-flex align-items-center p-4">
              <div
                className="stats-icon bg-primary-soft text-primary me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
              >
                <FaUser size={24} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">{total}</h4>
                <div className="text-muted small fw-semibold">
                  Total Contacts
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 bg-white overflow-hidden h-100">
            <Card.Body className="d-flex align-items-center p-4">
              <div
                className="stats-icon bg-success-soft text-success me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
              >
                <FaCheckCircle size={24} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">Active</h4>
                <div className="text-muted small fw-semibold">
                  System Status
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm rounded-4 bg-white overflow-hidden h-100">
            <Card.Body className="d-flex align-items-center p-4">
              <div
                className="stats-icon bg-warning-soft text-warning me-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{ width: "56px", height: "56px" }}
              >
                <FaCalendarAlt size={24} />
              </div>
              <div>
                <h4 className="fw-bold mb-0">Recent</h4>
                <div className="text-muted small fw-semibold">
                  New Inquiries
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Table Section */}
      <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-5">
        <Card.Header className="bg-white border-bottom p-3">
          <Row className="align-items-center">
            <Col md={3} className="d-none d-md-block">
              <h5 className="mb-0 fw-bold px-2">Contact List</h5>
            </Col>
            <Col md={3}>
              <Form.Select
                size="sm"
                className="w-auto cursor-pointer"
                value={limit}
                onChange={(e) =>
                  setSearchParams({
                    page: 1,
                    limit: Number(e.target.value),
                    search,
                  })
                }
              >
                <option value={10}>Show 10</option>
                <option value={25}>Show 25</option>
                <option value={50}>Show 50</option>
                <option value={100}>Show 100</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <InputGroup className="bg-light rounded-pill border-0 overflow-hidden px-2 py-1">
                <InputGroup.Text className="bg-transparent border-0 text-muted">
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search name, email, phone..."
                  className="bg-transparent border-0 shadow-none py-2"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              {isFetching && (
                <div className="text-end text-muted extra-small mt-1 px-3">
                  Updating results...
                </div>
              )}
            </Col>
          </Row>
        </Card.Header>
        <div className="table-responsive">
          <Table hover className="align-middle mb-0 custom-table">
            <thead className="bg-light text-muted small text-uppercase fw-bold">
              <tr>
                <th className="ps-4 border-0">S.No</th>
                <th className="border-0">User Info</th>
                <th className="border-0">Service</th>
                <th className="border-0">Message</th>
                <th className="border-0">Received On</th>
                <th className="text-end pe-4 border-0">Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts && contacts.length > 0 ? (
                contacts.map((contact, index) => (
                  <tr key={contact.id}>
                    <td className="ps-4">
                      <span className="text-muted small">
                        {(page - 1) * limit + index + 1}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div
                          className="contact-avatar bg-light me-3 rounded-3 d-flex align-items-center justify-content-center text-primary fw-bold"
                          style={{ width: "40px", height: "40px" }}
                        >
                          {contact.name?.substring(0, 1).toUpperCase() || (
                            <FaUser size={14} />
                          )}
                        </div>
                        <div>
                          <div className="fw-bold text-dark">
                            {contact.name}
                          </div>
                          <div className="text-muted extra-small d-flex align-items-center gap-2">
                            <a
                              href={`mailto:${contact.email}`}
                              className="text-decoration-none text-muted d-flex align-items-center gap-1"
                            >
                              <FaEnvelope size={10} /> {contact.email}
                            </a>
                            <FaCopy
                              className="cursor-pointer text-primary-soft-hover"
                              size={10}
                              title="Copy Email"
                              onClick={() => {
                                navigator.clipboard.writeText(contact.email);
                                message.success("Email copied to clipboard!");
                              }}
                            />
                          </div>
                          <div className="text-muted extra-small d-flex align-items-center gap-2">
                            <a
                              href={`tel:${contact.phone}`}
                              className="text-decoration-none text-muted d-flex align-items-center gap-1"
                            >
                              <FaPhoneAlt size={10} /> {contact.phone}
                            </a>
                            <FaCopy
                              className="cursor-pointer text-primary-soft-hover"
                              size={10}
                              title="Copy Phone"
                              onClick={() => {
                                navigator.clipboard.writeText(contact.phone);
                                message.success(
                                  "Phone number copied to clipboard!",
                                );
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge
                        bg="info-light"
                        className="text-info border-0 px-2 py-1 rounded small fw-normal"
                      >
                        {contact.service || "N/A"}
                      </Badge>
                      <div className="extra-small text-muted mt-1">
                        via {contact.contactMethod || "Web"}
                      </div>
                    </td>
                    <td>
                      <div
                        className="text-truncate"
                        style={{ maxWidth: "200px" }}
                        title={contact.message}
                      >
                        {contact.message || "-"}
                      </div>
                    </td>
                    <td>
                      <div className="small text-dark">
                        {new Date(contact.createdAt).toLocaleDateString()}
                      </div>
                      <div className="extra-small text-muted">
                        {new Date(contact.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="text-end pe-4">
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          variant="white"
                          size="sm"
                          onClick={() => handleView(contact)}
                          className="btn-action shadow-sm border"
                          title="View Message"
                        >
                          <FaEye className="text-primary" />
                        </Button>
                        <Button
                          variant="white"
                          size="sm"
                          onClick={() => handleRemove(contact)}
                          className="btn-action shadow-sm border"
                          title="Remove Contact"
                        >
                          <FaTrashAlt className="text-danger" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-5">
                    <div className="py-4">
                      <FaAddressBook size={48} className="text-light mb-3" />
                      <h5 className="text-muted fw-bold">No Contacts Found</h5>
                      <p className="text-muted small">
                        {search
                          ? `No results for "${search}"`
                          : "No inquiries received yet."}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Message Modal */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        className="fade-in"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-dark">
            <FaEnvelope className="text-primary me-2" />
            Inquiry Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {selectedContact && (
            <div className="modern-modal-content">
              <Row className="g-4">
                <Col md={12}>
                  <div className="bg-light p-4 rounded-4 mb-4">
                    <h6 className="text-muted extra-small fw-bold mb-3">
                      User Message
                    </h6>
                    <p
                      className="lead text-dark mb-0 fs-5"
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      "{selectedContact.message || "No message provided."}"
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-3 border rounded-3 h-100">
                    <h6 className="text-muted extra-small fw-bold mb-3">
                      Sender Details
                    </h6>
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <FaUser size={14} className="text-primary" />
                        <span className="fw-bold">{selectedContact.name}</span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <FaEnvelope size={14} className="text-primary" />
                        <span>{selectedContact.email}</span>
                        <FaCopy
                          className="cursor-pointer text-primary-soft-hover"
                          size={10}
                          title="Copy Email"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              selectedContact.email,
                            );
                            message.success("Email copied to clipboard!");
                          }}
                        />
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <FaPhoneAlt size={14} className="text-primary" />
                        <span>{selectedContact.phone}</span>
                        <FaCopy
                          className="cursor-pointer text-primary-soft-hover"
                          size={10}
                          title="Copy Phone"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              selectedContact.phone,
                            );
                            message.success(
                              "Phone number copied to clipboard!",
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-3 border rounded-3 h-100">
                    <h6 className="text-muted extra-small fw-bold mb-3">
                      Service Interest
                    </h6>
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <Badge
                          bg="info-light"
                          className="text-info px-3 py-2 rounded-pill fw-normal"
                        >
                          {selectedContact.service || "General Inquiry"}
                        </Badge>
                      </div>
                      <div className="text-muted small mt-2">
                        <strong>Source:</strong>{" "}
                        {selectedContact.contactMethod || "Web Form"}
                      </div>
                      <div className="text-muted small">
                        <strong>Received:</strong>{" "}
                        {new Date(selectedContact.createdAt)?.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={handleClose}
            className="rounded-pill px-4"
          >
            Close
          </Button>
          {selectedContact && (
            <Button
              variant="primary"
              onClick={() => {
                window.location.href = `mailto:${selectedContact.email}`;
              }}
              className="rounded-pill px-4 shadow-sm"
            >
              Reply via Email
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Pagination UI */}
      {totalPages > 1 && (
        <Pagination className="justify-content-center my-4 custom-pagination">
          <Pagination.Prev
            disabled={page === 1}
            onClick={() =>
              setSearchParams({
                page: page - 1,
                limit,
                search,
              })
            }
          />

          {[...Array(totalPages)].map((_, i) => (
            <Pagination.Item
              key={i + 1}
              active={page === i + 1}
              onClick={() =>
                setSearchParams({
                  page: i + 1,
                  limit,
                  search,
                })
              }
            >
              {i + 1}
            </Pagination.Item>
          ))}

          <Pagination.Next
            disabled={page === totalPages}
            onClick={() =>
              setSearchParams({
                page: page + 1,
                limit,
                search,
              })
            }
          />
        </Pagination>
      )}
      {/* Delete Modal */}
      <DeleteModel
        DELETESTATE={showDeleteModal}
        ONCLICK={handleCloseDeleteModal}
        YES={handleDeleteConfirm}
        DELETETITLE="Remove Contact"
        DESCRIPTION={`Are you sure you want to remove this contact inquiry from the list?`}
        loadTest="Removing..."
      />
    </div>
  );
};

export default Contacts;
